import { useQuery } from '@tanstack/react-query';
import { TopAgent, TopAgentResult } from '../../interfaces/Analytics';
import { getTopAgents } from '../../services/dashboardService';
import { Agent } from '../../interfaces/Agent';

interface UseTopAgentsParams {
  companyId: string | undefined;
  userId?: string;
  startDate: string | undefined;
  endDate: string | undefined;
  agentsList: Agent[] | undefined;
  enabled?: boolean;
}

const remapTopAgents = (
  topAgentsResult: TopAgentResult[],
  agentsList: Agent[]
): TopAgent[] => {
  return topAgentsResult.map(agent => {
    const agentDetails = agentsList.find(
      agentsListItem => agentsListItem.id === agent.agent_id
    );
    return {
      agentName: agentDetails?.name ?? '',
      image: agentDetails?.avatar ?? '',
      duration: agent.total_calls_duration,
      calls: agent.total_calls_number,
      successRate: agent.total_success_rate,
      voice: agentDetails?.voice ?? 'female',
    };
  });
};

export const useTopAgents = ({
  companyId,
  userId,
  startDate,
  endDate,
  agentsList,
  enabled = true,
}: UseTopAgentsParams) => {
  return useQuery<TopAgent[], Error>({
    queryKey: ['top-agents', companyId, userId, startDate, endDate],
    queryFn: async () => {
      if (!companyId || !startDate || !endDate) {
        return [];
      }
      const topAgentsData = await getTopAgents(
        companyId,
        startDate,
        endDate,
        userId
      );
      const topAgentsResult = topAgentsData.message.top_agents;

      if (!agentsList || agentsList.length === 0) {
        return [];
      }

      return remapTopAgents(topAgentsResult, agentsList);
    },
    enabled:
      enabled &&
      !!companyId &&
      !!startDate &&
      !!endDate &&
      !!agentsList &&
      agentsList.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
    refetchOnMount: false, // Don't refetch when navigating between pages
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
    refetchOnReconnect: false, // Don't refetch when reconnecting
  });
};
